// MaintenanceSalaryCombinationV2Module.js
import React, { useCallback, useRef, useState } from "react";
import MaintenanceSalaryCombinationV2Table from "./components/MaintenanceSalaryCombinationV2Table";
import OperationBenefitGroupTable from "./components/OperationBenefitGroupTable";
import RateAddEditModal from "./components/RateAddEditModal";
import OperationBenefitsAddEditModal from "./components/OperationBenefitsAddEditModal";
import { Button } from "../../../../common/APICommon";
import AddCardIcon from '@mui/icons-material/AddCard';
import LayersIcon from '@mui/icons-material/Layers';
import PostAddIcon from '@mui/icons-material/PostAdd';
import EditIcon from '@mui/icons-material/Edit';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SettingsIcon from '@mui/icons-material/Settings';
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DownloadIcon from '@mui/icons-material/Download';

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { DynamicFilterBar } from "../../filters/DynamicFilterBar";
import { FEATURES } from "../../../../config/features";
import { useOperationBenefitGroups } from "../../../hooks/useOperationBenefitGroups";
import OperationBenefitGroupModal from "./components/OperationBenefitGroupModal";
import MaintenanceSalaryCombinationV2UploadModal from "./components/MaintenanceSalaryCombinationV2UploadModal";
import { useMaintenanceSalaryCombinationCode } from "../../../hooks/useMaintenanceSalaryCombinationCode";
import { useEnumsApi } from "../../../hooks/useEnumsApi";
import { LoadingSpinner } from "../../../../common/Loading";
import { useOperationBenefits } from "../../../hooks/useOperationBenefits";


const initialModalState = {
  isOpen: false,
  mode: "Add"
};

const MaintenanceSalaryCombinationV2Module = ({
  user,
  allRules, // use only for uploading since we need to check if identity of each of the item
  rules, // Standard Rate rules from hook
  benefitGroups, // Grouped benefits from hook
  loading,
  refresh,
  saveRateRule,
  saveBenefit,
  uploadBulkData,
  showGeneralMessage,
  filterManager
}) => {

  const [showSettings, setShowSettings] = useState(false);
  const settingRef = useRef(null);

  const {
    activeFilterState,
    additionalApiFilterPayload,
    onFilterChange,
    applyFilters,
    resetFilters,
    getFiltersAsDetails,
    getOtherFilterAsDetails
  } = filterManager;

  const {
    data: allOperationBenefitsData,
    loading: allRulesLoading,
  } = useOperationBenefits({ user, showGeneralMessage });

  const {
    groups: allBenefitGroups,
    selectOptions,
    loading: groupsLoading,
    saveGroup
  } = useOperationBenefitGroups({ user, showGeneralMessage });

  const {
    data: mscCodes,
    loading: mscCodeLoading,
  } = useMaintenanceSalaryCombinationCode({ showGeneralMessage });

  const {
    loading: enumsApiLoading,
    fetchMaintenanceDetailedData
  } = useEnumsApi();

  // -- Modal States --
  const [rateModal, setRateModal] = useState(initialModalState);
  const [showMSCUploadForm, setShowMSCUploadForm] = useState(false);

  const [operationBenefitModal, setOperationBenefitModal] = useState(initialModalState);
  const [operationBenefitGroupModal, setOperationBenefitGroupModalOpen] = useState(initialModalState);
  const [selectedRateItem, setSelectedRateItem] = useState(null);
  const [selectedBenefitItem, setSelectedBenefitItem] = useState(null);
  const [selectedOperationBenefitGroupItem, setSelectedOperationBenefitGroupItem] = useState(null);

  const [currentRatePage, setCurrentRatePage] = useState(1);

  const validateSalaryFilters = (activeFilterState) => {
    const selectedEmpType = activeFilterState["EMPLOYEE_TYPE"];

    // 1. Mandatory Check: If no Employee Type is selected, it's always invalid
    if (!selectedEmpType) {
      return false;
    }

    // 2. Check the Disregard Flag
    // If true (Onshore): We only care about the Employee Type (which we already confirmed exists)
    // If false (Offshore): We must ensure all other filters in FEATURES are filled
    const disregardOthers = selectedEmpType?.maintenanceSalaryCombinationDisregardOtherFilter === true;

    if (disregardOthers) {
      // If we disregard others, we are "Ready" because Employee Type is already selected
      return true;
    }

    // 3. Full Validation (Offshore Mode)
    // Ensure every filter defined in the FEATURES list has a value in the state
    return FEATURES.MAINTENANCE_SALARY_FILTERS.every((key) => {
      const value = activeFilterState[key];

      // A filter is considered "filled" if it's not null and not undefined
      return value !== null && value !== undefined;
    });
  };

  // -- Settings Handlers --
  const generateDownloadTemplate = async () => {
    const workbook = new ExcelJS.Workbook();

    // Helper function to apply styling to headers
    const styleHeader = (worksheet, columns) => {
      worksheet.columns = columns.map(col => ({ ...col, width: 35 }));
      const headerRow = worksheet.getRow(1);

      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "000000" } };
        cell.alignment = { horizontal: "center" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "92d050" }, // Your specific green color
        };
      });
    };

    // --- SHEET 1: MaintenanceSalaryCombinations ---
    const sheet1 = workbook.addWorksheet("MaintenanceSalaryCombinations");
    const headers1 = [
      "EffectivityDate",
      "EmployeeType",
      "DepartmentCode",
      "PositionCode",
      "MaintenanceSalaryCombinationCode",
      "MaintenanceSalaryCombinationDailyRate"
    ];
    sheet1.addRow(headers1);
    styleHeader(sheet1, headers1.map(h => ({ header: h })));

    // Sample Data Row
    sheet1.addRow([
      "<01/01/2025>",
      "<On-Shore>",
      "<UDK123>",
      "<F>",
      "<F1>",
      "<480>"
    ]);

    // --- SHEET 2: OperationBenefitsGroup ---
    const sheet2 = workbook.addWorksheet("OperationBenefitsGroup");
    const headers2 = ["Description"];
    sheet2.addRow(headers2);
    styleHeader(sheet2, headers2.map(h => ({ header: h })));

    // Sample Data Row
    sheet2.addRow(["<GROUP 1>"]);

    // --- SHEET 3: OperationBenefits ---
    const sheet3 = workbook.addWorksheet("OperationBenefits");
    const headers3 = [
      "EffectivityDate",
      "EmployeeType",
      "DepartmentCode",
      "PositionCode",
      "MaintenanceSalaryCombinationCode",
      "OperationBenefitAmount",
    ];
    sheet3.addRow(headers3);
    styleHeader(sheet3, headers3.map(h => ({ header: h })));

    // Sample Data Row
    sheet3.addRow([
      "<01/01/2025>",
      "<On-Shore>",
      "<UDK123>",
      "<F>",
      "<F1>",
      "<480>"
    ]);

    // Generate and Download
    try {
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, "Dynamic_Rate_Configuration_Template.xlsx");
    } catch (error) {
      console.error("Error generating Excel template:", error);
    }
  };

  const openMSCUploadForm = () => {
    setShowMSCUploadForm(true);
  }
  const closeMSCUploadForm = () => {
    setShowMSCUploadForm(false);
  }

  // -- Rate Handlers --
  const handleRateEdit = (item) => {
    const prefilledOtherFilterDetails = getOtherFilterAsDetails();
    setSelectedRateItem({
      ...item,
      effectiveDate: item?.effectiveDate ?? prefilledOtherFilterDetails?.effectiveDate ?? null,
    });
    setRateModal({ isOpen: true, mode: "Edit" });
  };

  const handleRateSave = async (form) => {
    const res = await saveRateRule(selectedRateItem?.id, form, user.id);
    if (res.success) setRateModal(initialModalState);
  };

  const handleOpenRateModal = () => {
    if (!validateSalaryFilters(activeFilterState)) {
      showGeneralMessage("Please fill up the necessary filters", false);
      return;
    }

    const prefilledDetails = getFiltersAsDetails();
    const prefilledOtherFilterDetails = getOtherFilterAsDetails();

    setSelectedRateItem({
      category: "",
      salary: "",
      allowance: 0,
      details: prefilledDetails,
      effectiveDate: prefilledOtherFilterDetails?.effectiveDate,
    });

    setRateModal({ isOpen: true, mode: "Add" });
  };

  const handleCloseRateModal = () => {
    setRateModal(initialModalState);
    setSelectedRateItem(null);
  };

  // -- Operation Benefit Handlers --
  const handleBenefitEdit = (item) => {
    const prefilledOtherFilterDetails = getOtherFilterAsDetails();
    setSelectedBenefitItem({
      ...item,
      effectiveDate: item?.effectiveDate ?? prefilledOtherFilterDetails?.effectiveDate ?? null,
    });
    setOperationBenefitModal({ isOpen: true, mode: "Edit" });
  };

  const handleBenefitSave = async (form) => {
    const res = await saveBenefit(selectedBenefitItem?.id, form, user.id);
    if (res.success) setOperationBenefitModal(initialModalState);
  };

  const handleOpenBenefitModal = () => {
    if (!validateSalaryFilters(activeFilterState)) {
      showGeneralMessage("Please fill up the necessary filters first.", false);
      return;
    }

    if (!rules || rules.length === 0) {
      return showGeneralMessage(
        "No salary rates found for these filters. Please create a 'New Rate' first.",
        false
      );
    }

    const prefilledOtherFilterDetails = getOtherFilterAsDetails();

    setSelectedBenefitItem({
      rate: 0,
      operationBenefitGroupId: "",
      maintenanceSalaryCombinationV2Id: "",
      effectiveDate: prefilledOtherFilterDetails?.effectiveDate ?? null,
    });

    setOperationBenefitModal({
      isOpen: true,
      mode: "Add"
    });
  };

  const handleCloseOperationBenefitModal = () => {
    setOperationBenefitModal(initialModalState);
    setSelectedBenefitItem(null);
  };
  // -- Operation Benefit Group Handlers --
  const handleOpenOperationBenefitGroupModal = () => {
    setOperationBenefitGroupModalOpen({ isOpen: true, mode: "Add" });
  }
  const handleOperationBenefitGroupEdit = (item) => {
    setSelectedOperationBenefitGroupItem(item);
    setOperationBenefitGroupModalOpen({ isOpen: true, mode: "Edit" });
  };
  const handleCloseOperationBenefitGroupModal = (item) => {
    setSelectedOperationBenefitGroupItem(null);
    setOperationBenefitGroupModalOpen(initialModalState);
  };
  const handleOperationBenefitGroupSave = async (form) => {
    const res = await saveGroup(selectedOperationBenefitGroupItem?.id, form)
    if (res.success) {
      setOperationBenefitGroupModalOpen(initialModalState);
      await refresh();
    }
  }

  const handleGenerate = async () => {
    if (!validateSalaryFilters(activeFilterState)) {
      showGeneralMessage("Please fill up the necessary filters", false);
      return;
    }
    applyFilters();
  };

  const EmptyDashboardState = () => (
    <div className="w-full flex flex-col items-center justify-center py-16 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl mx-3">
      <div className="bg-white p-2 rounded-full shadow-sm mb-2">
        <FilterAltIcon className="text-gray-300" sx={{ fontSize: 25 }} />
      </div>
      <h3 className="text-gray-600 font-bold text-lg mb-1">No Data to Display</h3>
      <p className="text-gray-400 text-sm max-w-xs text-center">
        Apply filter criteria above to load salary rules and linked operation benefits.
      </p>
    </div>
  );

  return (
    <>
      <div>
        <div className="w-full py-2 px-4 box-border">
          <div className="text-sm">
            <span className="text-gray-400">{"Salary Module > "}</span>
            <span className="border-b border-black text-gray-700">{"Maintenance Salary Combinations"}</span>
          </div>
          <div className="flex items-center justify-between border-b bg-white">
            <h3 className="text-xl font-bold m-0 text-customMainColor tracking-tight uppercase">
              Maintenance Salary Combinations (Criteria-Based)
            </h3>
            <div className="flex items-center gap-3">
              <div className="relative flex items-center" ref={settingRef}>
                <button onClick={() => setShowSettings(!showSettings)} className="flex items-center gap-1 bg-blue-800 p-2 rounded hover:bg-blue-900 border-none cursor-pointer shadow-sm transition-all">
                  <SettingsIcon className="text-white" fontSize="small" />
                  <KeyboardArrowDownIcon className="text-white" fontSize="small" />
                </button>
                {showSettings && (
                  <div className="absolute right-0 top-full mt-2 w-52 bg-white shadow-2xl border border-gray-200 rounded-lg z-50 overflow-hidden animate-in fade-in zoom-in duration-100">
                    <button
                      onClick={generateDownloadTemplate}
                      className="w-full flex items-center justify-start gap-2 px-4 py-3 text-left hover:bg-blue-50 border-none bg-transparent cursor-pointer text-gray-700 transition-colors"
                    >
                      <DownloadIcon fontSize="small" className="text-blue-500 shrink-0" />
                      <span className="text-sm font-medium">Download Template</span>
                    </button>
                    <button
                      onClick={openMSCUploadForm}
                      className="w-full flex items-center justify-start gap-2 px-4 py-3 text-left hover:bg-blue-50 border-none bg-transparent cursor-pointer text-gray-700 transition-colors"
                    >
                      <AccountTreeIcon fontSize="small" className="text-amber-500 shrink-0" />
                      <span className="text-sm font-medium">Upload Data</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='p-4 space-y-6 relative'>
          {/* 1. Dynamic Filter Bar Section */}
          <div className="py-0 px-3">
            <div className="bg-white rounded-lg border border-gray-200 mt-2 p-4" style={{ border: "1px solid #d7d7d7" }}>
              <span className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-4 block">Filter Rates</span>
              <DynamicFilterBar
                requestedFilters={FEATURES.MAINTENANCE_SALARY_FILTERS}
                activeFilterState={activeFilterState}
                onFilterChange={onFilterChange}
                showButtons={false}
                allowAll={false}
              />
              <div className="flex gap-2 mt-5 border-t pt-4">
                <Button name="GENERATE" className="bg-blue-600 text-white px-5 py-2 border-none rounded cursor-pointer font-bold text-xs" onClick={handleGenerate} />
                <Button name="CLEAR" className="bg-gray-400 text-white px-5 py-2 border-none rounded cursor-pointer text-xs" onClick={resetFilters} />
              </div>
            </div>
          </div>

          {/* 2. Action Buttons */}
          <div className="flex justify-end items-center gap-2 px-3">
            <Button
              icon={<AddCardIcon />}
              name={"New Rate"}
              className={"bg-customGreen hover:bg-customGreenHover border-none cursor-pointer shadow-md"}
              onClick={handleOpenRateModal}
            />
            <Button
              icon={<PostAddIcon />}
              name={"New Operation Benefit"}
              className={"bg-blue-600 hover:bg-blue-700 border-none cursor-pointer shadow-md"}
              onClick={handleOpenBenefitModal}
            />
            <Button
              icon={<LayersIcon />}
              name={"New Operation Benefit Group"}
              className={"bg-blue-600 hover:bg-blue-700 border-none cursor-pointer shadow-md"}
              onClick={handleOpenOperationBenefitGroupModal}
            />
          </div>

          {/* 3. Horizontal Dashboard (Rates & Operation Benefits) */}
          <div className="flex flex-row gap-6 overflow-x-auto pb-6 px-3 custom-horizontal-scrollbar items-start">
            {rules.length > 0 ? (
              <>
                <div className="min-w-[450px] flex-shrink-0 bg-white p-5 rounded-xl">
                  <div className="mb-4 border-b border-gray-100 pb-3">
                    <h4 className="m-0 text-gray-600 font-extrabold uppercase tracking-tight text-sm">Employee Rate Table</h4>
                  </div>
                  <MaintenanceSalaryCombinationV2Table
                    data={rules}
                    onEdit={handleRateEdit}
                    loading={loading}
                    currentPage={currentRatePage}
                    setCurrentPage={setCurrentRatePage}
                  />
                </div>

                {benefitGroups?.map((group) => (
                  <div key={group.id} className="min-w-[400px] flex-shrink-0 p-5 rounded-xl">
                    <div className="mb-4 border-b border-gray-200 pb-3 flex justify-between items-center">
                      <h4 className="m-0 flex items-center text-blue-800 font-extrabold uppercase cursor-pointer tracking-tight text-sm" onClick={() => handleOperationBenefitGroupEdit(group)}>
                        {group.description}
                        <span className="ml-1 text-green-500 hover:text-green-700">
                          <EditIcon fontSize="small" />
                        </span>
                      </h4>

                      <div className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded text-[9px] font-bold">
                        BENEFITS
                      </div>
                    </div>
                    <OperationBenefitGroupTable
                      data={group.benefits || []}
                      onEdit={handleBenefitEdit}
                      loading={loading}
                    />
                  </div>
                ))}
              </>
            ) : <EmptyDashboardState />}
          </div>
        </div>

        <RateAddEditModal
          mode={rateModal.mode}
          isOpen={rateModal.isOpen}
          onClose={handleCloseRateModal}
          selectedItem={selectedRateItem}
          onSave={handleRateSave}
        />

        <OperationBenefitsAddEditModal
          mode={operationBenefitModal.mode}
          isOpen={operationBenefitModal.isOpen}
          selectOptions={selectOptions}
          loading={groupsLoading}
          onClose={handleCloseOperationBenefitModal}
          selectedItem={selectedBenefitItem}
          onSave={handleBenefitSave}
          rules={rules}
        />

        <OperationBenefitGroupModal
          mode={operationBenefitGroupModal.mode}
          isOpen={operationBenefitGroupModal.isOpen}
          selectedItem={selectedOperationBenefitGroupItem}
          onClose={handleCloseOperationBenefitGroupModal}
          onSave={handleOperationBenefitGroupSave}
        />

        <MaintenanceSalaryCombinationV2UploadModal
          user={user}
          isOpen={showMSCUploadForm}
          onClose={closeMSCUploadForm}
          onUpload={uploadBulkData}
          allRules={allRules}
          allOperationBenefitsData={allOperationBenefitsData}
          allBenefitGroups={allBenefitGroups}
          mscCodes={mscCodes}
          fetchMaintenanceDetailedData={fetchMaintenanceDetailedData}
          showGeneralMessage={showGeneralMessage}
        />
      </div>

      <LoadingSpinner loading={loading || allRulesLoading || groupsLoading || mscCodeLoading || enumsApiLoading} />

    </>
  );
};

export default MaintenanceSalaryCombinationV2Module;